import { useAuth0 } from "@auth0/auth0-react";
import { PageLayout } from "../../page-layout";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { PageLoader } from "../../page-loader";
import {
  isAuthSessionError,
  isReauthInProgressError,
  withTokenRecovery,
} from "../../../utils/tokenUtils";

const Auth0Dashboard = () => {
  const {
    user: auth0User,
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  let isLoading = auth0IsLoading;
  let user = auth0User;

  const [credits, setCredits] = useState("");
  const [isAccountReady, setAccountReady] = useState(true);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [isReauthing, setIsReauthing] = useState(false);

  const intervalRef = useRef(null);
  const authApiCall = useMemo(
    () =>
      withTokenRecovery({
        getAccessTokenSilently,
        loginWithRedirect,
        audience: process.env.REACT_APP_DEV_PORTAL_API_SERVER,
        context: "dashboard",
        returnTo: "/dashboard",
      }),
    [getAccessTokenSilently, loginWithRedirect]
  );

  const makeHttpError = (message, status) => {
    const error = new Error(message);
    error.status = status;
    return error;
  };

  const checkBalance = useCallback(async () => {
    try {
      const response = await authApiCall(async (token) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        try {
          return await fetch(
            `${process.env.REACT_APP_DEV_PORTAL_API_SERVER}/customer/balance`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              signal: controller.signal,
            }
          );
        } finally {
          clearTimeout(timeoutId);
        }
      });

      if (response.status === 401 || response.status === 403) {
        throw makeHttpError("Session expired.", response.status);
      }

      if (!response.ok) {
        throw makeHttpError(
          `Server error (${response.status}). Please try again.`,
          response.status
        );
      }

      if (response.status === 202) {
        setAccountReady(false);
        return 0;
      }

      if (response.status === 200) {
        setAccountReady(true);
      }

      const balanceRes = await response.json();
      return balanceRes.balance;
    } catch (error) {
      if (isReauthInProgressError(error)) {
        setIsReauthing(true);
        throw error;
      }
      setIsReauthing(false);
      if (error.name === "AbortError") {
        throw new Error("Request timed out. Please check your connection.");
      }
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError")
      ) {
        throw new Error("Network error. Please check your connection and try again.");
      }
      console.error("Balance check failed:", error);
      throw error;
    }
  }, [authApiCall]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Initial balance check
    setLoading(true);
    setError(null);

    checkBalance()
      .then((balance) => {
        setCredits(-balance / 2.5);
        setError(null);
        setIsReauthing(false);
      })
      .catch((err) => {
        if (isReauthInProgressError(err)) return;
        console.error("Initial balance check failed:", err);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });

    // Set up interval for periodic checks (less frequent to reduce load)
    intervalRef.current = setInterval(() => {
      // Only do background updates if no error state
      if (!error) {
        checkBalance()
          .then((balance) => {
            setCredits(-balance / 2.5);
            setError(null);
            setIsReauthing(false);
          })
          .catch((err) => {
            if (!isReauthInProgressError(err)) {
              console.error("Periodic balance check failed:", err);
            }
          });
      }
    }, 30000); // Check every 30 seconds instead of 10

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoading, user?.email, checkBalance, error]);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);

    checkBalance()
      .then((balance) => {
        setCredits(-balance / 2.5);
        setError(null);
        setIsReauthing(false);
      })
      .catch((err) => {
        if (isReauthInProgressError(err)) return;
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [checkBalance]);

  const getDisplayErrorMessage = (currentError) => {
    if (!currentError) return "";
    if (isAuthSessionError(currentError)) {
      return "Session expired. Re-authenticating...";
    }
    if (
      currentError.message?.includes("timed out") ||
      currentError.message?.includes("Network error")
    ) {
      return currentError.message;
    }
    return "Unable to load balance. Please try again.";
  };

  return (
    <PageLayout>
      <>
        <section className="text-white mb-[5rem] flex justify-center items-center flex-col rounded-2xl">
          <span className="text-accent text-[7rem] font-bold">
            {loading ? (
              <PageLoader />
            ) : credits || credits === 0 ? (
              credits
            ) : (
              <PageLoader />
            )}
          </span>
          <span className="font-bold">
            {loading ? (
              "Loading..."
            ) : isReauthing ? (
              "Re-authenticating..."
            ) : error ? (
              "Error loading balance"
            ) : !isAccountReady ? (
              "Please wait while we finish setting up your account..."
            ) : (
              "requests remaining"
            )}
          </span>
          {error && (
            <div className="text-red-400 text-sm mt-2 text-center">
              {getDisplayErrorMessage(error)}
              <br />
              <button
                onClick={handleRetry}
                className="mt-2 text-blue-400 underline hover:text-blue-300"
              >
                Try Again
              </button>
            </div>
          )}
        </section>
      </>
    </PageLayout>
  );
};

export default Auth0Dashboard;
